import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Link, useHistory, useLocation, useParams } from 'react-router-dom';
import Icon from '../../../hive/ui/Icon';
import { useFairTracker } from '../data/FairTrackerStore';
import {
  Block,
  Check,
  Chips,
  Confirm,
  Empty,
  Field,
  Input,
  Modal,
  NumInput,
  Pill,
  Segment,
  Select,
  Stars,
  Textarea,
  Thumb,
  useToast,
} from '../ui';
import { PhotoGrid, VideoGrid, VoiceList } from '../media/MediaGrid';
import useMedia from '../media/useMedia';
import useRecorder from '../media/useRecorder';
import { NoteModal } from './EntryScreen';
import { newContact, newFollowUp } from '../data/entities';
import {
  CERT_STATES,
  CERT_TYPES,
  CONTACT_TAGS,
  CURRENCIES,
  DECISIONS,
  FU_STATUS,
  FU_TYPES,
  HANDOVER,
  LEN_UNITS,
  NEXT_STEPS,
  PAY_METHODS,
  PRICE_UNITS,
  SAMPLE_STATES,
  SUPPLIER_TYPES,
  WT_UNITS,
} from '../data/constants';
import {
  autoFollowUp,
  bestQuoted,
  dateLabel,
  digits,
  landed,
  marginCalc,
  marginTone,
  mainPhoto,
  money,
  normalizePrimary,
  num,
  pendingFollowUps,
  primaryContact,
  priceLine,
  riskFlags,
  supplierDecision,
  supplierRating,
  syncShortlist,
} from '../rules';
import ReportViewer from '../reports/ReportViewer';
import { shareSupplierText } from '../reports/share';
import { p } from '../routes';
import { tr } from '../i18n';

const TABS = [
  ['overview', 'Overview'],
  ['products', 'Products'],
  ['photos', 'Photos'],
  ['contacts', 'Contacts'],
  ['pricing', 'Pricing'],
  ['notes', 'China Notes'],
  ['review', 'Final Review'],
  ['follow', 'Follow-Up'],
];

/**
 * Supplier profile (spec § 5.6) — eight tabs over one record.
 *
 * The tab strip scrolls horizontally and its items must NOT shrink (see
 * .ft-tab-btn): eight compressed tabs are unreadable on a phone, and this is
 * the screen a buyer opens standing in front of the booth.
 */
export default function SupplierScreen() {
  const { id } = useParams();
  const history = useHistory();
  const location = useLocation();
  const toast = useToast();
  const { fairs, suppliers, settings, saveSupplier, deleteSupplier } = useFairTracker();

  const supplier = useMemo(() => suppliers.find((s) => s.id === id) || null, [suppliers, id]);
  const fair = useMemo(
    () => (supplier ? fairs.find((f) => f.id === supplier.fairId) || null : null),
    [fairs, supplier],
  );

  const params = new URLSearchParams(location.search);
  const tab = params.get('tab') || 'overview';
  const setTab = (t) => history.replace(`${p(`/s/${id}`)}?tab=${t}`);

  const [confirming, setConfirming] = useState(false);
  const [report, setReport] = useState(false);

  if (!supplier) {
    return (
      <div className="ft-page">
        <Empty
          title={tr("That supplier is gone")}
          action={
            <Link className="ft-btn" to={p('/suppliers')}>
              {tr("Back to my suppliers")}
            </Link>
          }
        >
          {tr("It may have been deleted on another device.")}
        </Empty>
      </div>
    );
  }

  /** Every tab mutates through this, so S1 is re-applied on every write. */
  const write = async (patch) => {
    try {
      await saveSupplier(syncShortlist({ ...supplier, ...patch }));
    } catch (e) {
      toast('Could not save on this device');
    }
  };

  const remove = async () => {
    setConfirming(false);
    try {
      await deleteSupplier(supplier.id);
      toast('Supplier deleted');
      history.push(p('/suppliers'));
    } catch (e) {
      toast('Could not delete');
    }
  };

  const contact = primaryContact(supplier);
  const photo = mainPhoto(supplier);

  return (
    <div className="ft-page">
      <section className="ft-card ft-prohero">
        <Thumb photo={photo} initials={supplier.company} size={64} />
        <div className="ft-prohero-main">
          <h1 className="ft-h1">{supplier.company}</h1>
          <p className="ft-sub" style={{ margin: '2px 0 6px' }}>
            {[
              fair && fair.name,
              supplier.phase ? `Phase ${supplier.phase}` : null,
              supplier.booth ? `Booth ${supplier.booth}` : null,
              supplier.type,
              contact && contact.name,
            ]
              .filter(Boolean)
              .join(' · ')}
          </p>
          <div className="ft-supcard-pills">
            <Pill>{supplierDecision(supplier)}</Pill>
            {supplierRating(supplier) > 0 && <Stars value={supplierRating(supplier)} readOnly />}
            {supplier.inShortlist && <Pill tone="info">{tr("Shortlist")}</Pill>}
            {supplier.decision && <Pill>{supplier.decision}</Pill>}
          </div>
        </div>
        <div className="ft-prohero-actions">
          <button type="button" className="ft-btn ft-btn-sm" onClick={() => setReport(true)}>
            <Icon name="Printer" size={14} /> {tr("PDF")}
          </button>
          <button
            type="button"
            className="ft-btn ft-btn-sm"
            onClick={() => shareSupplierText(supplier, fair, toast)}
          >
            <Icon name="Share2" size={14} /> {tr("Share")}
          </button>
        </div>
      </section>

      <nav className="ft-tabs" aria-label="Supplier sections">
        {TABS.map(([key, label]) => (
          <button
            key={key}
            type="button"
            className={`ft-tab-btn${tab === key ? ' ft-tab-btn-on' : ''}`}
            onClick={() => setTab(key)}
            aria-current={tab === key ? 'page' : undefined}
          >
            {tr(label)}
            {key === 'follow' && pendingFollowUps(supplier) > 0 ? ` (${pendingFollowUps(supplier)})` : ''}
          </button>
        ))}
      </nav>

      {tab === 'overview' && (
        <Overview
          supplier={supplier}
          fair={fair}
          fairs={fairs}
          write={write}
          onDelete={() => setConfirming(true)}
        />
      )}
      {tab === 'products' && <Products supplier={supplier} fair={fair} settings={settings} write={write} />}
      {tab === 'photos' && <Photos supplier={supplier} write={write} />}
      {tab === 'contacts' && <Contacts supplier={supplier} write={write} />}
      {tab === 'pricing' && <Pricing supplier={supplier} fair={fair} settings={settings} write={write} />}
      {tab === 'notes' && <ChinaNotes supplier={supplier} write={write} />}
      {tab === 'review' && <ReviewSummary supplier={supplier} />}
      {tab === 'follow' && <FollowUps supplier={supplier} write={write} />}

      {report && (
        <ReportViewer kind="supplier" supplier={supplier} fair={fair} onClose={() => setReport(false)} />
      )}

      {confirming && (
        <Confirm
          title={`Delete ${supplier.company}?`}
          message={tr("Every product, photo, contact and note goes with it. This cannot be undone.")}
          confirmLabel={tr("Delete supplier")}
          onConfirm={remove}
          onCancel={() => setConfirming(false)}
        />
      )}
    </div>
  );
}

/* ═══════════════════════════════════════════════════════════════════════════
   OVERVIEW
   ═════════════════════════════════════════════════════════════════════════ */

function Overview({ supplier, fair, fairs, write, onDelete }) {
  const l = landed(supplier);
  const best = bestQuoted(supplier, fair);
  const products = supplier.products || [];
  const sample = products.map((x) => x.sample).find((s) => s && s !== 'No') || 'No';
  const cert = products.map((x) => x.cert).find((c) => c === 'Yes') || products[0]?.cert || '—';
  const next = products.map((x) => x.nextStep).find((n) => n && n !== 'None') || 'None';

  return (
    <div className="ft-profile-cols">
      <section className="ft-card">
        <h2 className="ft-h2">{tr("Details")}</h2>
        <Field label={tr("Company")}>
          <Input value={supplier.company} onChange={(v) => write({ company: v })} />
        </Field>
        <div className="ft-grid-2">
          <Field label={tr("Booth")}>
            <Input value={supplier.booth} onChange={(v) => write({ booth: v })} />
          </Field>
          <Field label={tr("Hall")}>
            <Input value={supplier.hall} onChange={(v) => write({ hall: v })} />
          </Field>
        </div>
        <Field label={tr("Company type")}>
          <Segment value={supplier.type} onChange={(v) => write({ type: v })} options={SUPPLIER_TYPES} />
        </Field>
        <Field label={tr("Phase")}>
          <Segment value={supplier.phase} onChange={(v) => write({ phase: v })} options={['1', '2', '3']} />
        </Field>
        {fairs.length > 1 && (
          <Field label={tr("Fair")} hint={tr("Moving a supplier moves all of its products.")}>
            <Select
              value={supplier.fairId}
              options={fairs.map((f) => ({ value: f.id, label: f.name }))}
              onChange={(v) => write({ fairId: v })}
            />
          </Field>
        )}
        {/* This toggle is the MANUAL flag, not the shortlist state itself.
            Labelling it "In final shortlist" read as a contradiction: the hero
            showed a Shortlist pill (earned by a Strong product) while this said
            "No". The label now says what it actually controls, and the hint
            reports the current state so the two never look like they disagree. */}
        <Field
          label={tr("Keep on the shortlist manually")}
          hint={
            supplier.inShortlist && !supplier.manualShortlist
              ? 'Already on the shortlist because a product is Strong (rule S1). Turn this on to keep it there even if that changes.'
              : 'Keeps this supplier on the shortlist even with no Strong product.'
          }
        >
          <Segment
            value={supplier.manualShortlist ? 'Yes' : 'No'}
            options={['Yes', 'No']}
            onChange={(v) =>
              write({ manualShortlist: v === 'Yes', inShortlist: v === 'Yes' ? true : supplier.inShortlist })
            }
          />
        </Field>
      </section>

      <section className="ft-card">
        <h2 className="ft-h2">{tr("At a glance")}</h2>
        <dl className="ft-glance">
          <div>
            <dt>{tr("Visited")}</dt>
            <dd>{supplier.createdAt ? new Date(supplier.createdAt).toLocaleDateString() : '—'}</dd>
          </div>
          <div>
            <dt>{tr("Products")}</dt>
            <dd>{products.length}</dd>
          </div>
          <div>
            <dt>{tr("Best quoted")}</dt>
            <dd>{best != null ? money(best) : '—'}</dd>
          </div>
          <div>
            <dt>{tr("Sample")}</dt>
            <dd>{tr(sample)}</dd>
          </div>
          <div>
            <dt>{tr("Certification")}</dt>
            <dd>{tr(cert)}</dd>
          </div>
          <div>
            <dt>{tr("Next step")}</dt>
            <dd>{tr(next)}</dd>
          </div>
          <div>
            <dt>{tr("Landed / unit")}</dt>
            <dd>{l.perUnit != null ? money(l.perUnit) : '—'}</dd>
          </div>
          <div>
            <dt>{tr("Final decision")}</dt>
            <dd>{supplier.decision ? tr(supplier.decision) : '—'}</dd>
          </div>
          <div>
            <dt>{tr("Follow-ups")}</dt>
            <dd>{pendingFollowUps(supplier)}</dd>
          </div>
        </dl>

        <div className="ft-btnrow" style={{ marginTop: 14 }}>
          <Link className="ft-btn ft-btn-primary" to={`${p('/entry')}?supplier=${supplier.id}`}>
            <Icon name="Plus" size={16} /> {tr('Add product')}
          </Link>
          {supplier.inShortlist && (
            <Link className="ft-btn" to={p(`/review/${supplier.id}`)}>
              {tr("Open final review")}
            </Link>
          )}
          <button type="button" className="ft-btn ft-btn-quiet" onClick={onDelete}>
            <Icon name="Trash2" size={15} /> {tr("Delete supplier")}
          </button>
        </div>
      </section>
    </div>
  );
}

/* ═══════════════════════════════════════════════════════════════════════════
   PRODUCTS — list, with a full inline edit form
   ═════════════════════════════════════════════════════════════════════════ */

function Products({ supplier, fair, settings, write }) {
  const [editing, setEditing] = useState(null);
  const [confirming, setConfirming] = useState(null);
  const products = supplier.products || [];

  const saveProduct = async (next) => {
    // Re-run F1/F2 so a next step or revisit set here also creates its
    // follow-up, exactly as Quick Entry does.
    const updated = { ...supplier, products: products.map((x) => (x.id === next.id ? next : x)) };
    updated.followUps = autoFollowUp(updated, next);
    await write({ products: updated.products, followUps: updated.followUps });
    setEditing(null);
  };

  const removeProduct = async () => {
    const victim = confirming;
    setConfirming(null);
    await write({ products: products.filter((x) => x.id !== victim.id) });
  };

  return (
    <>
      {!products.length && <Empty title={tr("No products yet")}>{tr("Add the first one from Quick Entry.")}</Empty>}

      <div className="ft-list">
        {products.map((prod) => (
          <article className="ft-card" key={prod.id}>
            <div className="ft-prodrow">
              <Thumb photo={mainPhoto(supplier, prod.id)} initials={prod.name} size={48} />
              <div className="ft-rowcard-main">
                <b>{prod.name}</b>
                <div className="ft-faint">
                  {[prod.category, priceLine(prod, fair), prod.moq ? `MOQ ${prod.moq}` : null]
                    .filter(Boolean)
                    .join(' · ')}
                </div>
                <div className="ft-supcard-pills">
                  <Pill>{prod.decision}</Pill>
                  {prod.rating > 0 && <Stars value={prod.rating} readOnly />}
                  {prod.revisit && <Pill tone="maybe">↻ Revisit</Pill>}
                </div>
              </div>
              <button
                type="button"
                className="ft-btn ft-btn-sm"
                onClick={() => setEditing(editing === prod.id ? null : prod.id)}
              >
                {editing === prod.id ? 'Close' : 'Edit'}
              </button>
            </div>

            {editing === prod.id && (
              <ProductForm
                product={prod}
                supplier={supplier}
                fair={fair}
                settings={settings}
                onSave={saveProduct}
                onClose={() => setEditing(null)}
                onDelete={() => setConfirming(prod)}
              />
            )}
          </article>
        ))}
      </div>

      <Link
        className="ft-btn ft-btn-primary ft-btn-block"
        style={{ marginTop: 12 }}
        to={`${p('/entry')}?supplier=${supplier.id}`}
      >
        <Icon name="Plus" size={17} /> {tr("Add another product")}
      </Link>

      {confirming && (
        <Confirm
          title={`Delete "${confirming.name}"?`}
          message={tr("The product and its notes go. Photos stay with the supplier.")}
          confirmLabel={tr("Delete product")}
          onConfirm={removeProduct}
          onCancel={() => setConfirming(null)}
        />
      )}
    </>
  );
}

/** The full product form — every field from spec § 3.3. */
function ProductForm({ product, supplier, fair, settings, onSave, onClose, onDelete }) {
  const toast = useToast();
  const [prod, setProd] = useState(product);
  const [noteOpen, setNoteOpen] = useState(false);
  const set = (k) => (v) => setProd((x) => ({ ...x, [k]: v }));

  const media = useMedia({ supplierId: supplier.id, productId: product.id });
  const recorder = useRecorder({
    onDone: async ({ blob, duration }) => {
      const next = await media.addVoice({ existing: prod.voiceNotes || [], blob, duration });
      setProd((x) => ({ ...x, voiceNotes: next }));
      toast('Voice note saved');
    },
  });

  const margin = marginCalc(prod, fair, settings);

  return (
    <div className="ft-prodform">
      <div className="ft-grid-2">
        <Field label={tr("Product name")}>
          <Input value={prod.name} onChange={set('name')} />
        </Field>
        <Field label={tr("Category")}>
          <Input value={prod.category} onChange={set('category')} />
        </Field>
        <Field label={tr("Quoted price")}>
          <NumInput value={prod.price} onChange={set('price')} />
        </Field>
        <Field label={tr("Currency")}>
          <Select value={prod.currency} options={CURRENCIES} onChange={set('currency')} />
        </Field>
        <Field label={tr("Price is per")}>
          <Select value={prod.priceUnit} options={PRICE_UNITS} onChange={set('priceUnit')} />
        </Field>
        <Field label={tr("MOQ")}>
          <NumInput value={prod.moq} onChange={set('moq')} />
        </Field>
        <Field label={tr("Best price negotiated")}>
          <NumInput value={prod.bestPrice} onChange={set('bestPrice')} />
        </Field>
        <Field label={tr("Quantity for that price")}>
          <NumInput value={prod.qty} onChange={set('qty')} />
        </Field>
        <Field label={tr("Quote valid until")}>
          <Input type="date" value={prod.validUntil} onChange={set('validUntil')} />
        </Field>
        <Field label={tr("I would sell it at (USD)")}>
          <NumInput value={prod.sellPrice} onChange={set('sellPrice')} />
        </Field>
      </div>

      {margin && (
        <div className="ft-landed">
          <div>
            <span>Est. landed (×{margin.factor})</span>
            <b>{money(margin.landedEst)}</b>
          </div>
          <div>
            <span>{tr("Margin")}</span>
            <b className={`ft-tone-${marginTone(margin.margin)}`}>{margin.margin.toFixed(0)}%</b>
          </div>
          <div>
            <span>{tr("Profit / unit")}</span>
            <b className={`ft-tone-${marginTone(margin.margin)}`}>{money(margin.profit)}</b>
          </div>
        </div>
      )}

      <Field label={tr("Description")}>
        <Input value={prod.description} onChange={set('description')} />
      </Field>
      <Field label={tr("Notes")}>
        <Textarea value={prod.notes} onChange={set('notes')} />
      </Field>
      <div className="ft-btnrow">
        <button type="button" className="ft-btn ft-btn-sm" onClick={() => setNoteOpen(true)}>
          <Icon name="FileText" size={15} /> {tr("Add note")}
        </button>
        <button
          type="button"
          className={`ft-btn ft-btn-sm${recorder.recording ? ' ft-btn-danger' : ''}`}
          onClick={recorder.toggle}
        >
          <Icon name="Mic" size={15} />
          {recorder.recording ? `Stop ${recorder.seconds}s` : 'Voice note'}
        </button>
      </div>
      <VoiceList
        notes={prod.voiceNotes || []}
        onRemove={async (v) => {
          const next = await media.remove(prod.voiceNotes || [], v);
          setProd((x) => ({ ...x, voiceNotes: next }));
        }}
      />

      <Field label={tr("Rating")}>
        <Stars value={prod.rating} onChange={set('rating')} size={26} />
      </Field>
      <Field label={tr("Decision")}>
        <Segment value={prod.decision} onChange={set('decision')} options={DECISIONS} tone="decision" />
      </Field>
      <Field label={tr("Next step")}>
        <Segment value={prod.nextStep} onChange={set('nextStep')} options={NEXT_STEPS} />
      </Field>
      <Check checked={prod.revisit} onChange={set('revisit')} label={tr("Revisit this booth")} tone="amber" />
      {prod.revisit && (
        <div className="ft-grid-2" style={{ marginTop: 8 }}>
          <Field label={tr("Reason to return")}>
            <Input value={prod.revisitReason} onChange={set('revisitReason')} />
          </Field>
          <Field label={tr("Appointment")}>
            <Input type="datetime-local" value={prod.revisitAt} onChange={set('revisitAt')} />
          </Field>
        </div>
      )}

      <Block title={tr("Terms & certification")}>
        <div className="ft-grid-2">
          <Field label={tr("FOB port")}>
            <Input value={prod.fobPort} onChange={set('fobPort')} />
          </Field>
          <Field label={tr("Payment terms")}>
            <Input value={prod.paymentTerms} onChange={set('paymentTerms')} />
          </Field>
          <Field label={tr("Production time")}>
            <Input value={prod.productionTime} onChange={set('productionTime')} />
          </Field>
        </div>
        <Field label={tr("Sample")}>
          <Segment value={prod.sample} onChange={set('sample')} options={SAMPLE_STATES} />
        </Field>
        <Field label={tr("US certification")}>
          <Segment value={prod.cert} onChange={set('cert')} options={CERT_STATES} />
        </Field>
        <Chips value={prod.certTypes || []} onChange={set('certTypes')} options={CERT_TYPES} />
      </Block>

      <Block title={tr("Carton & shipping")}>
        <div className="ft-grid-3">
          <Field label={tr("Carton L")}>
            <NumInput value={prod.cartonL} onChange={set('cartonL')} />
          </Field>
          <Field label={tr("Carton W")}>
            <NumInput value={prod.cartonW} onChange={set('cartonW')} />
          </Field>
          <Field label={tr("Carton H")}>
            <NumInput value={prod.cartonH} onChange={set('cartonH')} />
          </Field>
          <Field label={tr("Unit")}>
            <Select value={prod.lenUnit} options={LEN_UNITS} onChange={set('lenUnit')} />
          </Field>
          <Field label={tr("Carton weight")}>
            <NumInput value={prod.cartonWeight} onChange={set('cartonWeight')} />
          </Field>
          <Field label={tr("Unit")}>
            <Select value={prod.wtUnit} options={WT_UNITS} onChange={set('wtUnit')} />
          </Field>
          <Field label={tr("Units / carton")}>
            <NumInput value={prod.unitsPerCarton} onChange={set('unitsPerCarton')} />
          </Field>
        </div>
      </Block>

      <Block title={tr("Money paid at this booth")}>
        <div className="ft-grid-2">
          <Field label={tr("Sample paid")}>
            <NumInput value={prod.samplePaid} onChange={set('samplePaid')} />
          </Field>
          <Field label={tr("Paid with")}>
            <Select value={prod.samplePayMethod} placeholder="—" options={PAY_METHODS} onChange={set('samplePayMethod')} />
          </Field>
          <Field label={tr("Deposit paid")}>
            <NumInput value={prod.depositPaid} onChange={set('depositPaid')} />
          </Field>
          <Field label={tr("Paid with")}>
            <Select value={prod.depositMethod} placeholder="—" options={PAY_METHODS} onChange={set('depositMethod')} />
          </Field>
        </div>
        <Field label={tr("Sample handover")}>
          <Segment value={prod.sampleHandover} onChange={set('sampleHandover')} options={HANDOVER} />
        </Field>
        <Field label={tr("Pick-up / ship date")}>
          <Input type="date" value={prod.samplePickup} onChange={set('samplePickup')} />
        </Field>
      </Block>

      <div className="ft-btnrow" style={{ marginTop: 12 }}>
        <button type="button" className="ft-btn ft-btn-green" onClick={() => onSave(prod)}>
          {tr("Save product")}
        </button>
        <button type="button" className="ft-btn ft-btn-cancel" onClick={onClose}>
          {tr("Close")}
        </button>
        <button type="button" className="ft-btn ft-btn-quiet" onClick={onDelete}>
          <Icon name="Trash2" size={15} /> {tr("Delete product")}
        </button>
      </div>

      {noteOpen && (
        <NoteModal
          title={tr("Add note")}
          hint={tr("Type, or use your keyboard's dictation key.")}
          onClose={() => setNoteOpen(false)}
          onSave={(text) => {
            setProd((x) => ({ ...x, notes: [x.notes, text].filter(Boolean).join('\n') }));
            setNoteOpen(false);
          }}
        />
      )}
    </div>
  );
}

/* ═══════════════════════════════════════════════════════════════════════════
   PHOTOS / VIDEOS
   ═════════════════════════════════════════════════════════════════════════ */

function Photos({ supplier, write }) {
  const media = useMedia({ supplierId: supplier.id });
  const [forProduct, setForProduct] = useState('');
  const products = supplier.products || [];

  return (
    <>
      <section className="ft-card">
        <h2 className="ft-h2">{tr("Photos")}</h2>
        {products.length > 1 && (
          <Field label={tr("Attach to product")}>
            <Select
              value={forProduct}
              placeholder={tr("Not linked to a product")}
              options={products.map((x) => ({ value: x.id, label: x.name }))}
              onChange={setForProduct}
            />
          </Field>
        )}
        <div className="ft-btnrow">
          <button
            type="button"
            className="ft-btn ft-btn-primary"
            onClick={async () =>
              write({
                photos: await media.addPhotos({
                  existing: supplier.photos || [],
                  fromCamera: true,
                  forProductId: forProduct,
                }),
              })
            }
          >
            <Icon name="Camera" size={16} /> {tr("Take photo")}
          </button>
          <button
            type="button"
            className="ft-btn"
            onClick={async () =>
              write({
                photos: await media.addPhotos({ existing: supplier.photos || [], forProductId: forProduct }),
              })
            }
          >
            <Icon name="Upload" size={16} /> {tr("Upload")}
          </button>
        </div>
        <PhotoGrid
          photos={supplier.photos || []}
          products={products}
          onRetag={(ph, tag, pid) => write({ photos: media.retag(supplier.photos || [], ph, tag, pid) })}
          onRemove={async (ph) => write({ photos: await media.remove(supplier.photos || [], ph) })}
        />
      </section>

      <section className="ft-card">
        <h2 className="ft-h2">{tr("Videos")}</h2>
        <div className="ft-btnrow">
          <button
            type="button"
            className="ft-btn ft-btn-primary"
            onClick={async () =>
              write({ videos: await media.addVideo({ existing: supplier.videos || [], forProductId: forProduct }) })
            }
          >
            <Icon name="Video" size={16} /> {tr("Record video")}
          </button>
        </div>
        <VideoGrid
          videos={supplier.videos || []}
          onRemove={async (v) => write({ videos: await media.remove(supplier.videos || [], v) })}
        />
      </section>
    </>
  );
}

/* ═══════════════════════════════════════════════════════════════════════════
   CONTACTS
   ═════════════════════════════════════════════════════════════════════════ */

function Contacts({ supplier, write }) {
  const toast = useToast();
  const [editing, setEditing] = useState(null);
  // A contact's WeChat ID and phone were captured once, at a booth, from a
  // person who may be unreachable afterwards — so removal is confirmed.
  const [removing, setRemoving] = useState(null);
  const contacts = supplier.contacts || [];
  const sorted = [...contacts].sort((a, b) => (b.primary ? 1 : 0) - (a.primary ? 1 : 0));

  const saveContact = async (next) => {
    const exists = contacts.some((c) => c.id === next.id);
    const list = exists ? contacts.map((c) => (c.id === next.id ? next : c)) : [...contacts, next];
    await write({ contacts: normalizePrimary(list, next.id) });
    setEditing(null);
    toast('Contact saved');
  };

  const removeContact = async (id) => {
    await write({ contacts: normalizePrimary(contacts.filter((c) => c.id !== id)) });
  };

  return (
    <>
      {!contacts.length && <Empty title={tr("No contacts yet")}>{tr("Add the person you spoke to.")}</Empty>}

      <div className="ft-list">
        {sorted.map((c) => (
          <article className="ft-card" key={c.id}>
            <header className="ft-planhead">
              <div>
                <h2 className="ft-h2">{c.name || 'Unnamed contact'}</h2>
                {c.role && <p className="ft-sub" style={{ margin: 0 }}>{c.role}</p>}
              </div>
              {c.primary && <Pill tone="info">{tr("Primary")}</Pill>}
              <button type="button" className="ft-btn ft-btn-sm" onClick={() => setEditing(c)}>
                {tr("Edit")}
              </button>
            </header>

            <ContactLinks contact={c} supplier={supplier} />

            {(c.photos || []).length > 0 && (
              <div className="ft-mediagrid">
                {c.photos.map((ph) => (
                  <div className="ft-mediacell" key={ph.id}>
                    <Thumb photo={ph} size={84} rounded={false} alt={ph.tag} />
                    <span className="ft-mediatag">{ph.tag}</span>
                  </div>
                ))}
              </div>
            )}

            <button
              type="button"
              className="ft-btn ft-btn-quiet ft-btn-sm"
              style={{ marginTop: 8 }}
              onClick={() => setRemoving(c)}
            >
              <Icon name="Trash2" size={14} /> {tr("Remove contact")}
            </button>
          </article>
        ))}
      </div>

      <button
        type="button"
        className="ft-btn ft-btn-primary ft-btn-block"
        style={{ marginTop: 12 }}
        onClick={() => setEditing(newContact({ primary: !contacts.length }))}
      >
        <Icon name="Plus" size={17} /> {tr("New contact")}
      </button>

      {editing && (
        <ContactForm
          contact={editing}
          supplier={supplier}
          onSave={saveContact}
          onClose={() => setEditing(null)}
        />
      )}

      {removing && (
        <Confirm
          title={`Remove ${removing.name || 'this contact'}?`}
          message={tr("Their phone, WeChat and email go with them. You captured these once, at the booth.")}
          confirmLabel={tr("Remove contact")}
          onConfirm={() => {
            removeContact(removing.id);
            setRemoving(null);
          }}
          onCancel={() => setRemoving(null)}
        />
      )}
    </>
  );
}

/** Clickable actions (spec § 3.4). WhatsApp and tel: need digits only. */
function ContactLinks({ contact, supplier }) {
  const toast = useToast();
  const wa = digits(contact.whatsapp || contact.phone);
  const tel = digits(contact.phone);

  const copyWeChat = async () => {
    try {
      await navigator.clipboard.writeText(contact.wechat);
      toast('WeChat ID copied');
    } catch (e) {
      toast(contact.wechat);
    }
  };

  const saveVcard = async () => {
    const card = vcardText(supplier, contact);
    const blob = new Blob([card], { type: 'text/vcard' });
    const file = new File([blob], `${contact.name || 'contact'}.vcf`, { type: 'text/vcard' });
    try {
      if (navigator.canShare && navigator.canShare({ files: [file] })) {
        await navigator.share({ files: [file], title: contact.name });
        return;
      }
    } catch (e) {
      if (e && e.name === 'AbortError') return;
    }
    try {
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = file.name;
      a.click();
      URL.revokeObjectURL(url);
    } catch (e) {
      try {
        await navigator.clipboard.writeText(card);
        toast('Contact copied');
      } catch (e2) {
        toast('Could not save the contact');
      }
    }
  };

  return (
    <>
      <dl className="ft-kv">
        {contact.phone && (
          <>
            <dt>{tr("Phone")}</dt>
            <dd>{contact.phone}</dd>
          </>
        )}
        {contact.wechat && (
          <>
            <dt>WeChat</dt>
            <dd>{contact.wechat}</dd>
          </>
        )}
        {contact.whatsapp && (
          <>
            <dt>WhatsApp</dt>
            <dd>{contact.whatsapp}</dd>
          </>
        )}
        {contact.email && (
          <>
            <dt>{tr("Email")}</dt>
            <dd>{contact.email}</dd>
          </>
        )}
        {contact.website && (
          <>
            <dt>{tr("Website")}</dt>
            <dd>{contact.website}</dd>
          </>
        )}
        {contact.instagram && (
          <>
            <dt>{tr("Instagram")}</dt>
            <dd>{contact.instagram}</dd>
          </>
        )}
        {contact.linkedin && (
          <>
            <dt>LinkedIn</dt>
            <dd>{contact.linkedin}</dd>
          </>
        )}
        {contact.other && (
          <>
            <dt>{tr("Other")}</dt>
            <dd>{contact.other}</dd>
          </>
        )}
      </dl>

      <div className="ft-btnrow" style={{ marginTop: 10 }}>
        {tel && (
          <a className="ft-btn ft-btn-sm" href={`tel:${tel}`}>
            <Icon name="Phone" size={14} /> {tr("Call")}
          </a>
        )}
        {wa && (
          <a className="ft-btn ft-btn-sm" href={`https://wa.me/${wa}`} target="_blank" rel="noopener noreferrer">
            <Icon name="MessageCircle" size={14} /> WhatsApp
          </a>
        )}
        {contact.email && (
          <a className="ft-btn ft-btn-sm" href={`mailto:${contact.email}`}>
            <Icon name="Mail" size={14} /> {tr("Email")}
          </a>
        )}
        {contact.website && (
          <a
            className="ft-btn ft-btn-sm"
            href={contact.website.startsWith('http') ? contact.website : `https://${contact.website}`}
            target="_blank"
            rel="noopener noreferrer"
          >
            <Icon name="Globe" size={14} /> {tr("Website")}
          </a>
        )}
        {contact.instagram && (
          <a
            className="ft-btn ft-btn-sm"
            href={`https://instagram.com/${contact.instagram.replace(/^@/, '')}`}
            target="_blank"
            rel="noopener noreferrer"
          >
            {tr("Instagram")}
          </a>
        )}
        {contact.wechat && (
          <button type="button" className="ft-btn ft-btn-sm" onClick={copyWeChat}>
            <Icon name="Copy" size={14} /> {tr("Copy WeChat")}
          </button>
        )}
        <button type="button" className="ft-btn ft-btn-sm" onClick={saveVcard}>
          <Icon name="Download" size={14} /> {tr("Save contact")}
        </button>
      </div>
    </>
  );
}

/** vCard 3.0 (spec § 7). */
export function vcardText(supplier, contact) {
  const lines = [
    'BEGIN:VCARD',
    'VERSION:3.0',
    `N:${contact.name || ''}`,
    `FN:${contact.name || supplier.company || ''}`,
    `ORG:${supplier.company || ''}`,
  ];
  if (contact.role) lines.push(`TITLE:${contact.role}`);
  if (contact.phone) lines.push(`TEL;TYPE=CELL:${contact.phone}`);
  if (contact.whatsapp) lines.push(`TEL;TYPE=WhatsApp:${contact.whatsapp}`);
  if (contact.email) lines.push(`EMAIL:${contact.email}`);
  if (contact.website) lines.push(`URL:${contact.website}`);
  const note = [
    contact.wechat ? `WeChat: ${contact.wechat}` : null,
    supplier.booth ? `Booth: ${supplier.booth}` : null,
    contact.other || null,
  ]
    .filter(Boolean)
    .join(' | ');
  if (note) lines.push(`NOTE:${note}`);
  lines.push('END:VCARD');
  return lines.join('\r\n');
}

function ContactForm({ contact, supplier, onSave, onClose }) {
  const [c, setC] = useState(contact);
  const set = (k) => (v) => setC((x) => ({ ...x, [k]: v }));
  const media = useMedia({ supplierId: supplier.id, contactId: contact.id });

  return (
    <Modal
      title={contact.name ? 'Edit contact' : 'New contact'}
      onClose={onClose}
      actions={
        <>
          <button type="button" className="ft-btn ft-btn-cancel" onClick={onClose}>
            {tr("Cancel")}
          </button>
          <button type="button" className="ft-btn ft-btn-green" onClick={() => onSave(c)}>
            {tr("Save contact")}
          </button>
        </>
      }
    >
      <Field label={tr("Name")}>
        <Input value={c.name} onChange={set('name')} autoFocus />
      </Field>
      <Field label={tr("Role / title")}>
        <Input value={c.role} onChange={set('role')} />
      </Field>
      <div className="ft-grid-2">
        <Field label={tr("Phone")}>
          <Input value={c.phone} onChange={set('phone')} type="tel" />
        </Field>
        <Field label="WeChat">
          <Input value={c.wechat} onChange={set('wechat')} />
        </Field>
        <Field label="WhatsApp">
          <Input value={c.whatsapp} onChange={set('whatsapp')} type="tel" />
        </Field>
        <Field label={tr("Email")}>
          <Input value={c.email} onChange={set('email')} type="email" />
        </Field>
        <Field label={tr("Website")}>
          <Input value={c.website} onChange={set('website')} />
        </Field>
        <Field label={tr("Instagram")}>
          <Input value={c.instagram} onChange={set('instagram')} />
        </Field>
        <Field label="LinkedIn">
          <Input value={c.linkedin} onChange={set('linkedin')} />
        </Field>
        <Field label={tr("Other")}>
          <Input value={c.other} onChange={set('other')} />
        </Field>
      </div>
      <Field label={tr("Primary contact")} hint={tr("Used on cards, exports, share text and outreach.")}>
        <Segment
          value={c.primary ? 'Yes' : 'No'}
          options={['Yes', 'No']}
          onChange={(v) => set('primary')(v === 'Yes')}
        />
      </Field>

      <h3 className="ft-h2" style={{ marginTop: 12 }}>
        {tr("Card & QR photos")}
      </h3>
      <div className="ft-chips">
        {CONTACT_TAGS.map((tag) => (
          <button
            key={tag}
            type="button"
            className="ft-chip"
            onClick={async () => {
              const photos = await media.addPhotos({
                existing: c.photos || [],
                tag,
                fromCamera: true,
              });
              setC((x) => ({ ...x, photos }));
            }}
          >
            <Icon name="Camera" size={14} /> {tag}
          </button>
        ))}
      </div>
      <PhotoGrid
        photos={c.photos || []}
        onRemove={async (ph) => {
          const photos = await media.remove(c.photos || [], ph);
          setC((x) => ({ ...x, photos }));
        }}
      />
    </Modal>
  );
}

/* ═══════════════════════════════════════════════════════════════════════════
   PRICING
   ═════════════════════════════════════════════════════════════════════════ */

function Pricing({ supplier, fair, settings, write }) {
  const products = supplier.products || [];
  if (!products.length) return <Empty title={tr("No products to price")} />;

  return (
    <div className="ft-list">
      {products.map((prod) => {
        const margin = marginCalc(prod, fair, settings);
        const paid = num(prod.samplePaid) + num(prod.depositPaid);
        return (
          <article className="ft-card" key={prod.id}>
            <h2 className="ft-h2">{prod.name}</h2>
            <dl className="ft-kv">
              <dt>{tr("Quoted")}</dt>
              <dd>
                {priceLine(prod, fair) || '—'}
                {prod.qty ? ` @ ${prod.qty}` : ''}
              </dd>
              <dt>{tr("Best negotiated")}</dt>
              <dd>{prod.bestPrice ? money(num(prod.bestPrice), prod.currency) : '—'}</dd>
              <dt>{tr("MOQ")}</dt>
              <dd>{prod.moq || '—'}</dd>
              <dt>{tr("Valid until")}</dt>
              <dd>{prod.validUntil ? dateLabel(prod.validUntil) : '—'}</dd>
              <dt>{tr("FOB port")}</dt>
              <dd>{prod.fobPort || '—'}</dd>
              <dt>{tr("Payment")}</dt>
              <dd>{prod.paymentTerms || '—'}</dd>
              <dt>{tr("Production")}</dt>
              <dd>{prod.productionTime || '—'}</dd>
              <dt>{tr("Carton")}</dt>
              <dd>
                {prod.cartonL || prod.cartonW || prod.cartonH
                  ? `${prod.cartonL || '?'}×${prod.cartonW || '?'}×${prod.cartonH || '?'} ${prod.lenUnit}`
                  : '—'}
                {prod.cartonWeight ? ` · ${prod.cartonWeight}${prod.wtUnit}` : ''}
                {prod.unitsPerCarton ? ` · ${prod.unitsPerCarton}/carton` : ''}
              </dd>
            </dl>

            {margin && (
              <div className="ft-landed">
                <div>
                  <span>{tr("Est. landed")}</span>
                  <b>{money(margin.landedEst)}</b>
                </div>
                <div>
                  <span>{tr("Margin")}</span>
                  <b className={`ft-tone-${marginTone(margin.margin)}`}>{margin.margin.toFixed(0)}%</b>
                </div>
              </div>
            )}

            {paid > 0 && (
              <p className="ft-sub" style={{ marginTop: 10 }}>
                {tr("Paid at booth:")} <b>{money(paid)}</b>
                {prod.samplePayMethod ? ` · ${prod.samplePayMethod}` : ''}
                {prod.sampleHandover ? ` · ${prod.sampleHandover}` : ''}
              </p>
            )}

            {(prod.tiers || []).length > 0 && (
              <div className="ft-tablewrap">
                <table className="ft-table">
                  <thead>
                    <tr>
                      <th>{tr("Quantity")}</th>
                      <th>{tr("Price")}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {prod.tiers.map((t, i) => (
                      // eslint-disable-next-line react/no-array-index-key
                      <tr key={i}>
                        <td className="ft-num">{t.qty}</td>
                        <td className="ft-num">{money(num(t.price), prod.currency)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </article>
        );
      })}
    </div>
  );
}

/* ═══════════════════════════════════════════════════════════════════════════
   CHINA NOTES — supplier-level notes with a 400ms autosave
   ═════════════════════════════════════════════════════════════════════════ */

function ChinaNotes({ supplier, write }) {
  const [text, setText] = useState(supplier.chinaNotes || '');
  const timer = useRef(null);
  const [noteFor, setNoteFor] = useState(null);

  /* Autosave 400ms after typing stops (spec § 5.6). Debounced rather than
     per-keystroke because every write also appends to the sync outbox. */
  useEffect(() => {
    if (text === (supplier.chinaNotes || '')) return undefined;
    if (timer.current) clearTimeout(timer.current);
    timer.current = setTimeout(() => write({ chinaNotes: text }), 400);
    return () => timer.current && clearTimeout(timer.current);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [text]);

  const products = supplier.products || [];

  return (
    <>
      <section className="ft-card">
        <h2 className="ft-h2">{tr("Supplier notes")}</h2>
        <p className="ft-sub">{tr("Saves itself as you type.")}</p>
        <Textarea value={text} onChange={setText} rows={6} placeholder={tr("What else should I remember?")} />
      </section>

      {products.map((prod) => (
        <section className="ft-card" key={prod.id}>
          <h2 className="ft-h2">{prod.name}</h2>
          <div className="ft-supcard-pills">
            <Pill>{prod.decision}</Pill>
            {prod.rating > 0 && <Stars value={prod.rating} readOnly />}
            {prod.nextStep !== 'None' && <Pill tone="info">{tr(prod.nextStep)}</Pill>}
          </div>
          {prod.notes && <p className="ft-prewrap">{prod.notes}</p>}
          <VoiceList notes={prod.voiceNotes || []} />
          <button type="button" className="ft-btn ft-btn-sm" onClick={() => setNoteFor(prod)}>
            <Icon name="FileText" size={15} /> {tr("Add note")}
          </button>
        </section>
      ))}

      {noteFor && (
        <NoteModal
          title={`Note — ${noteFor.name}`}
          hint={tr("Type, or use your keyboard's dictation key.")}
          onClose={() => setNoteFor(null)}
          onSave={(t) => {
            write({
              products: products.map((x) =>
                x.id === noteFor.id ? { ...x, notes: [x.notes, t].filter(Boolean).join('\n') } : x,
              ),
            });
            setNoteFor(null);
          }}
        />
      )}
    </>
  );
}

/* ═══════════════════════════════════════════════════════════════════════════
   FINAL REVIEW summary
   ═════════════════════════════════════════════════════════════════════════ */

function ReviewSummary({ supplier }) {
  const l = landed(supplier);
  const flags = riskFlags(supplier);
  const r = supplier.review || {};

  if (!supplier.inShortlist) {
    return (
      <Empty title={tr("Not on the shortlist yet")}>
        Final Review is for finalists. Mark a product &quot;Strong&quot;, or add this supplier to the
        shortlist from the Overview tab.
      </Empty>
    );
  }

  return (
    <section className="ft-card">
      <h2 className="ft-h2">{tr("Final Review")}</h2>
      <dl className="ft-glance" style={{ marginTop: 10 }}>
        <div>
          <dt>{tr("Landed / unit")}</dt>
          <dd>{l.perUnit != null ? money(l.perUnit) : '—'}</dd>
        </div>
        <div>
          <dt>{tr("Total landed")}</dt>
          <dd>{l.total ? money(l.total) : '—'}</dd>
        </div>
        <div>
          <dt>{tr("Certification")}</dt>
          <dd>{r.c_status || 'Not set'}</dd>
        </div>
        <div>
          <dt>{tr("Sample")}</dt>
          <dd>{r.q_sampleReceived === 'Yes' ? (r.q_sampleApproved === 'Yes' ? 'Approved' : 'Received') : 'Not received'}</dd>
        </div>
        <div>
          <dt>{tr("Decision")}</dt>
          <dd>{supplier.decision || 'Not decided'}</dd>
        </div>
      </dl>

      {flags.length > 0 && (
        <ul className="ft-flags">
          {flags.map((f) => (
            <li key={f}>
              <Icon name="AlertTriangle" size={12} /> {f}
            </li>
          ))}
        </ul>
      )}

      <Link className="ft-btn ft-btn-primary ft-btn-block" style={{ marginTop: 12 }} to={p(`/review/${supplier.id}`)}>
        {tr("Open full review")}
      </Link>
    </section>
  );
}

/* ═══════════════════════════════════════════════════════════════════════════
   FOLLOW-UP
   ═════════════════════════════════════════════════════════════════════════ */

function FollowUps({ supplier, write }) {
  const [draft, setDraft] = useState(() => newFollowUp());
  const list = supplier.followUps || [];
  const set = (k) => (v) => setDraft((x) => ({ ...x, [k]: v }));

  const add = async () => {
    await write({ followUps: [...list, draft] });
    setDraft(newFollowUp());
  };

  const update = (id, patch) =>
    write({ followUps: list.map((f) => (f.id === id ? { ...f, ...patch } : f)) });

  const remove = (id) => write({ followUps: list.filter((f) => f.id !== id) });

  return (
    <>
      <section className="ft-card">
        <h2 className="ft-h2">{tr("New follow-up")}</h2>
        <Field label={tr("Type")}>
          <Select value={draft.type} options={FU_TYPES} onChange={set('type')} />
        </Field>
        <div className="ft-grid-2">
          <Field label={tr("Date")}>
            <Input type="date" value={draft.date} onChange={set('date')} />
          </Field>
          <Field label={tr("Time")}>
            <Input type="time" value={draft.time} onChange={set('time')} />
          </Field>
        </div>
        <Field label={tr("Status")}>
          <Select value={draft.status} options={FU_STATUS} onChange={set('status')} />
        </Field>
        <Field label={tr("Notes")}>
          <Input value={draft.notes} onChange={set('notes')} />
        </Field>
        <button type="button" className="ft-btn ft-btn-primary ft-btn-block" onClick={add}>
          {tr("Add follow-up")}
        </button>
      </section>

      {!list.length && <Empty title={tr("No follow-ups")} />}

      <div className="ft-list ft-list-rows" style={{ marginTop: 10 }}>
        {list.map((f) => (
          <div className="ft-rowcard" key={f.id}>
            <div className="ft-rowcard-main">
              <b>{f.type}</b>
              {f.auto && <Pill tone="info">{tr("auto")}</Pill>}
              <div className="ft-faint">
                {[dateLabel(f.date), f.time, f.notes].filter(Boolean).join(' · ')}
              </div>
            </div>
            <Select
              value={f.status}
              options={FU_STATUS}
              onChange={(v) => update(f.id, { status: v })}
            />
            <button type="button" className="ft-icon-btn" onClick={() => remove(f.id)} aria-label="Remove follow-up">
              <Icon name="Trash2" size={15} />
            </button>
          </div>
        ))}
      </div>
    </>
  );
}
